import { RootState } from '../../types';
import { DomainsEnabled, LifecycleState, MachineCoverage, StatusMap } from './treeTypes';
import { DOMAIN_ORDER, DomainCode } from '../../../main/platform/dashboards/summary/types';

// Live machine status (seeded from the tree at load, refreshed by the fleet poll). Subscribe with
// `useAppSelector(selectStatus)` and read a node's health via `nodeHealth(status, id)`.
export const selectStatus = (state: RootState): StatusMap | undefined => state.nodeTreeReducers.status;

export const nodeHealth = (status: StatusMap | undefined, id: number | undefined): number | null | undefined =>
    id == null ? undefined : status?.[id]?.health;

// Both lifecycle values that mean "the asset is out of service for maintenance". A window opened from
// scratch is `planned`; editing a live one re-reports it as `unplanned`.
export const MAINTENANCE_STATES = ['planned_maintenance', 'unplanned_maintenance'];

export const isMaintenanceState = (state: LifecycleState | undefined): boolean =>
    !!state && MAINTENANCE_STATES.includes(state);

export const nodeLifecycleState = (
    status: StatusMap | undefined,
    id: number | undefined,
): LifecycleState | undefined => (id == null ? undefined : status?.[id]?.lifecycle_state);

// True only during an active maintenance window (the asset greys out and leaves the health counters).
// Not the full cycle state: the backend auto-reverts the machine to `operational` once the window
// elapses, and the pending review is reported by the lifecycle `latest` endpoint instead.
export const nodeInMaintenance = (status: StatusMap | undefined, id: number | undefined): boolean =>
    isMaintenanceState(nodeLifecycleState(status, id));

// Scalar 0-100 health score (higher = better); null when not materialized, undefined before the
// first poll. Distinct from `nodeHealth` (the categorical 0..3 index).
export const nodeHealthScore = (status: StatusMap | undefined, id: number | undefined): number | null | undefined =>
    id == null ? undefined : status?.[id]?.health_score;

// Scalar 0-100 observability (two decimals on the wire); null when not materialized, undefined before
// the first poll. Comes from fleet_status at the same level as `health_score`.
export const nodeObservability = (status: StatusMap | undefined, id: number | undefined): number | null | undefined =>
    id == null ? undefined : status?.[id]?.observability;

export const nodeRunning = (status: StatusMap | undefined, id: number | undefined): string | undefined =>
    id == null ? undefined : status?.[id]?.running;

// Per-domain coverage for a machine (only populated after a group conditions poll).
export const nodeCoverage = (status: StatusMap | undefined, id: number | undefined): MachineCoverage | undefined =>
    id == null ? undefined : status?.[id]?.coverage;

// True when any of the machine's enabled offline techniques is due (drives OFF → gray).
export const nodeOfflineDue = (status: StatusMap | undefined, id: number | undefined): boolean =>
    id == null ? false : (status?.[id]?.offline ?? []).some((o) => o.is_due);

// Which domain chips to show for a machine (from its enable flags); undefined until a conditions poll.
export const nodeDomainsEnabled = (
    status: StatusMap | undefined,
    id: number | undefined,
): DomainsEnabled | undefined => (id == null ? undefined : status?.[id]?.domains_enabled);

export type CoverageChip = { domain: DomainCode; health: number | null };

// Resolve a machine's per-domain coverage chips to {domain, health-index|null}, shared by every card
// layout (each applies its own color system to the health index). Only ENABLED domains appear. AGE is
// always optimal (0); OFF is null (grey) when an offline analysis is due; ONL/PRC/OFF otherwise take
// their worst point health (null = unknown/grey). Empty until the group conditions poll has returned.
export const coverageChips = (
    coverage: MachineCoverage | undefined,
    offlineDue: boolean,
    domainsEnabled: DomainsEnabled | undefined,
): CoverageChip[] => {
    if (!domainsEnabled) return [];
    return DOMAIN_ORDER.filter((d) => domainsEnabled[d]).map((d) => {
        if (d === 'AGE') return { domain: d, health: 0 };
        if (d === 'OFF' && offlineDue) return { domain: d, health: null };
        return { domain: d, health: coverage?.[d as 'ONL' | 'PRC' | 'OFF'] ?? null };
    });
};
